import { ExternalLink } from "lucide-react";
import type { AdDef } from "@/data/adData";

interface Props {
  ad: AdDef;
}

const SponsoredLeaderboardRow = ({ ad }: Props) => {
  const Icon = ad.icon;

  return (
    <a
      href={ad.url}
      target="_blank"
      rel="noopener noreferrer sponsored"
      className="group block transition-colors border-b border-border last:border-b-0 ad-alive relative overflow-hidden"
      style={{
        background: `linear-gradient(90deg, hsl(var(--${ad.colorVar}) / 0.08), hsl(var(--${ad.colorVar}) / 0.02) 50%, transparent 80%)`,
      }}
    >
      {/* Shimmer overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `linear-gradient(90deg, transparent, hsl(var(--${ad.colorVar}) / 0.06), transparent)`,
          animation: 'ad-shimmer 6s ease-in-out infinite',
          width: '50%',
        }}
      />
      {/* Desktop — mirrors InfluencerCard grid */}
      <div className="hidden md:grid grid-cols-[60px_minmax(160px,1fr)_100px_100px_100px_100px_110px_140px_40px] items-center gap-4 px-5 py-4">
        {/* Rank area → empty */}
        <span />

        {/* Influencer area → Icon + Name + Handle */}
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
            style={{ background: `hsl(var(--${ad.colorVar}) / 0.15)` }}
          >
            <Icon className="w-5 h-5" style={{ color: `hsl(var(--${ad.colorVar}))` }} />
          </div>
          <div className="min-w-0">
            <span className="font-semibold text-foreground truncate flex items-center gap-1.5">
              {ad.title}
            </span>
            {ad.xHandle && (
              <span className="font-mono text-xs text-muted-foreground">
                @{ad.xHandle}
              </span>
            )}
          </div>
        </div>

        {/* Stats area → Ad badge + description, text wraps */}
        <div className="col-span-5 flex items-center gap-2 min-w-0">
          <span className="font-mono text-[8px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-muted text-muted-foreground shrink-0 leading-tight">
            Ad
          </span>
          <span className="text-xs text-muted-foreground line-clamp-2">
            {ad.tagline} — {ad.description}
          </span>
        </div>

        {/* CTA */}
        <span
          className="font-mono text-[11px] font-semibold flex items-center gap-1 shrink-0 justify-center whitespace-nowrap"
          style={{ color: `hsl(var(--${ad.colorVar}))` }}
        >
          {ad.cta}
          <ExternalLink className="w-3 h-3 opacity-60 group-hover:opacity-100 transition-opacity" />
        </span>

        {/* Arrow area */}
        <span />
      </div>

      {/* Mobile / Tablet */}
      <div className="md:hidden px-4 py-3">
        <div className="flex items-start gap-2.5">
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5"
            style={{ background: `hsl(var(--${ad.colorVar}) / 0.15)` }}
          >
            <Icon className="w-4 h-4" style={{ color: `hsl(var(--${ad.colorVar}))` }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="font-semibold text-xs text-foreground">{ad.title}</span>
              <span className="font-mono text-[8px] uppercase tracking-widest px-1 py-0.5 rounded bg-muted text-muted-foreground shrink-0">
                Ad
              </span>
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5">{ad.tagline} — {ad.description}</p>
          </div>
          <span
            className="font-mono text-[10px] font-semibold shrink-0 mt-0.5 whitespace-nowrap"
            style={{ color: `hsl(var(--${ad.colorVar}))` }}
          >
            {ad.cta}
          </span>
        </div>
      </div>
    </a>
  );
};

export default SponsoredLeaderboardRow;
